import { useEffect, useState } from 'react';
import {
  PhieuXuatTraLaiNhaCungCap,
  PhieuXuatTraLaiNhaCungCapChiTiet,
  PhieuXuatTraLaiNhaCungCapInput,
  PhieuXuatTraLaiNhaCungCapResponse
} from '@/types/schemas/phieu-xuat-tra-lai-nha-cung-cap.type';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UsePhieuXuatTraLaiNhaCungCapReturn {
  phieuXuatTraLaiNhaCungCaps: PhieuXuatTraLaiNhaCungCap[];
  isLoading: boolean;
  isLoadingDetail: boolean;
  addPhieuXuatTraLaiNhaCungCap: (
    newPhieuXuatTraLaiNhaCungCap: PhieuXuatTraLaiNhaCungCapInput
  ) => Promise<PhieuXuatTraLaiNhaCungCap>;
  updatePhieuXuatTraLaiNhaCungCap: (
    uuid: string,
    updatedPhieuXuatTraLaiNhaCungCap: PhieuXuatTraLaiNhaCungCapInput
  ) => Promise<PhieuXuatTraLaiNhaCungCap>;
  deletePhieuXuatTraLaiNhaCungCap: (uuid: string) => Promise<void>;
  refreshPhieuXuatTraLaiNhaCungCaps: () => Promise<void>;
  getPhieuXuatTraLaiNhaCungCapBySupplier: (supplierId: string) => Promise<PhieuXuatTraLaiNhaCungCap[]>;
  getPhieuXuatTraLaiNhaCungCapDetail: (uuid: string) => Promise<PhieuXuatTraLaiNhaCungCapChiTiet[]>;
}

/**
 * Hook for managing PhieuXuatTraLaiNhaCungCap (Supplier Return Voucher) data
 *
 * This hook provides functions to fetch, create, update, and delete supplier return vouchers and their details.
 */
export const usePhieuXuatTraLaiNhaCungCap = (
  initialPhieuXuatTraLaiNhaCungCaps: PhieuXuatTraLaiNhaCungCap[] = []
): UsePhieuXuatTraLaiNhaCungCapReturn => {
  const [phieuXuatTraLaiNhaCungCaps, setPhieuXuatTraLaiNhaCungCaps] = useState<PhieuXuatTraLaiNhaCungCap[]>(
    initialPhieuXuatTraLaiNhaCungCaps
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchPhieuXuatTraLaiNhaCungCaps = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<PhieuXuatTraLaiNhaCungCapResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_XUAT_TRA_LAI_NHA_CUNG_CAP}/`
      );
      setPhieuXuatTraLaiNhaCungCaps(response.data.results);
    } catch (error) {
      console.error('Error fetching supplier return vouchers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPhieuXuatTraLaiNhaCungCapBySupplier = async (supplierId: string): Promise<PhieuXuatTraLaiNhaCungCap[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<PhieuXuatTraLaiNhaCungCapResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_XUAT_TRA_LAI_NHA_CUNG_CAP}/?ma_kh=${supplierId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching supplier return vouchers by supplier:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const addPhieuXuatTraLaiNhaCungCap = async (
    newPhieuXuatTraLaiNhaCungCap: PhieuXuatTraLaiNhaCungCapInput
  ): Promise<PhieuXuatTraLaiNhaCungCap> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.post<PhieuXuatTraLaiNhaCungCap>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_XUAT_TRA_LAI_NHA_CUNG_CAP}/`,
        newPhieuXuatTraLaiNhaCungCap
      );

      const addedPhieuXuatTraLaiNhaCungCap = response.data;
      setPhieuXuatTraLaiNhaCungCaps(prev => [...prev, addedPhieuXuatTraLaiNhaCungCap]);
      return addedPhieuXuatTraLaiNhaCungCap;
    } catch (error) {
      console.error('Error adding supplier return voucher:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePhieuXuatTraLaiNhaCungCap = async (
    uuid: string,
    updatedPhieuXuatTraLaiNhaCungCap: PhieuXuatTraLaiNhaCungCapInput
  ): Promise<PhieuXuatTraLaiNhaCungCap> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<PhieuXuatTraLaiNhaCungCap>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_XUAT_TRA_LAI_NHA_CUNG_CAP}/${uuid}/`,
        updatedPhieuXuatTraLaiNhaCungCap
      );

      const updatedData = response.data;
      setPhieuXuatTraLaiNhaCungCaps(prev => prev.map(item => (item.uuid === uuid ? updatedData : item)));
      return updatedData;
    } catch (error) {
      console.error('Error updating supplier return voucher:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deletePhieuXuatTraLaiNhaCungCap = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_XUAT_TRA_LAI_NHA_CUNG_CAP}/${uuid}/`);
      setPhieuXuatTraLaiNhaCungCaps(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting supplier return voucher:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getPhieuXuatTraLaiNhaCungCapDetail = async (uuid: string): Promise<PhieuXuatTraLaiNhaCungCapChiTiet[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoadingDetail(true);
    try {
      const response = await api.get<PhieuXuatTraLaiNhaCungCap>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_XUAT_TRA_LAI_NHA_CUNG_CAP}/${uuid}/`
      );
      // Return the detail array from the response
      return (response.data as any).chi_tiet || [];
    } catch (error) {
      console.error('Error fetching supplier return voucher detail:', error);
      throw error;
    } finally {
      setIsLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchPhieuXuatTraLaiNhaCungCaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    phieuXuatTraLaiNhaCungCaps,
    isLoading,
    isLoadingDetail,
    addPhieuXuatTraLaiNhaCungCap,
    updatePhieuXuatTraLaiNhaCungCap,
    deletePhieuXuatTraLaiNhaCungCap,
    refreshPhieuXuatTraLaiNhaCungCaps: fetchPhieuXuatTraLaiNhaCungCaps,
    getPhieuXuatTraLaiNhaCungCapBySupplier,
    getPhieuXuatTraLaiNhaCungCapDetail
  };
};

export const fetchAllPhieuXuatTraLaiNhaCungCap = async (
  entitySlug: string,
  params: Record<string, any> = {},
  pageSize = 200
): Promise<PhieuXuatTraLaiNhaCungCap[]> => {
  let page = 1;
  let all: PhieuXuatTraLaiNhaCungCap[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.get<PhieuXuatTraLaiNhaCungCapResponse>(
      `/entities/${entitySlug}/erp/${QUERY_KEYS.PHIEU_XUAT_TRA_LAI_NHA_CUNG_CAP}/`,
      {
        params: {
          ...params,
          page,
          page_size: pageSize
        }
      }
    );

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
